<script lang="ts">
  import Icon from '../components/Icon.svelte';
  import { onMount } from 'svelte';
  import { push, replace } from 'svelte-spa-router';
  import { session } from '../lib/session';
  import { api, ApiError } from '../lib/api';
  import { adminState } from '../lib/admin-state';
  import MarkdownEditor from '../components/MarkdownEditor.svelte';
  import AudioPlayer from '../components/AudioPlayer.svelte';
  import BlockEditor from '../components/BlockEditor.svelte';
  import ChapterEditor from '../components/ChapterEditor.svelte';
  import Lightbox from '../components/Lightbox.svelte';
  import Avatar from '../components/Avatar.svelte';
  import ConfirmModal from '../components/ConfirmModal.svelte';
  import { renderMarkdown } from '../lib/markdown';
  import type { AdminCommentDTO, AdminMaterialDTO, AdminCategoryDTO, Block, MaterialChapter, MaterialType } from '../lib/types';
  import { formatDateTime } from '../lib/format';

  interface Props {
    params?: { id?: string };
  }
  let { params }: Props = $props();
  const materialId = $derived(params?.id ?? '');

  let loading = $state(true);
  let notFound = $state(false);
  let original = $state<AdminMaterialDTO | null>(null);
  let categories = $state<AdminCategoryDTO[] | null>(null);

  let title = $state('');
  let description = $state('');
  let categoryId = $state<string>('');
  let isPublished = $state(false);
  let body = $state<Block[]>([]);
  let chapters = $state<MaterialChapter[]>([]);
  // Flipped by ChapterEditor whenever its rows violate validation
  // (out-of-bounds times, empty titles, duplicate starts, etc.). Save is
  // disabled while this is false so admin can't ship broken chapters.
  let chaptersValid = $state(true);

  let saving = $state(false);
  let error = $state<string | null>(null);
  let alertOpen = $state(false);

  let deleteModalOpen = $state(false);
  let deleting = $state(false);
  let deleteError = $state<string | null>(null);

  // Playback URL for video/audio materials. Fetched once when the page loads
  // so the player can be shown inline in the form AND reused by the preview
  // modal (no double fetch).
  let playbackUrl = $state<string | null>(null);
  let playbackLoading = $state(false);
  let previewOpen = $state(false);

  // Lightbox inside the preview modal — clicking any image opens it full-screen.
  let lightbox = $state<{ images: string[]; index: number } | null>(null);
  function openLightbox(images: string[], index: number): void {
    lightbox = { images, index };
  }

  const selectedCategory = $derived(
    categoryId && categories ? categories.find((c) => c.id === categoryId) ?? null : null,
  );

  /** Tries to load the playback URL for the current material. No-ops for
   *  text materials and for video/audio rows that lost their Bunny binding. */
  async function loadPlayback(): Promise<void> {
    if (!original) return;
    if (original.type !== 'video' && original.type !== 'audio') return;
    if (original.bunnyMissingAt) return;
    if (playbackUrl) return;
    playbackLoading = true;
    try {
      const { iframeUrl } = await api.materialPlayback(materialId);
      playbackUrl = iframeUrl;
    } catch {
      /* silent — caller falls back to placeholder */
    } finally {
      playbackLoading = false;
    }
  }

  function openPreview(): void {
    previewOpen = true;
  }

  const TYPE_LABELS: Record<MaterialType, string> = {
    video: '▶️ Відео',
    audio: 'Аудіо',
    text: 'Текст',
  };

  function backToMaterials(): void {
    adminState.activeTab = 'content';
    adminState.contentSubTab = 'materials';
    push('/admin/dashboard');
  }

  onMount(async () => {
    if (!$session) {
      replace('/');
      return;
    }
    if (!$session.isAdmin) {
      replace('/home');
      return;
    }
    if (!materialId) {
      notFound = true;
      loading = false;
      return;
    }
    try {
      const [m, cats] = await Promise.all([
        api.adminMaterial(materialId),
        api.adminCategories(),
      ]);
      original = m;
      title = m.title;
      description = m.description ?? '';
      categoryId = m.category?.id ?? '';
      isPublished = m.isPublished;
      body = m.body;
      chapters = m.chapters;
      categories = cats.items;
      // Fire playback fetch in the background so the inline player has a URL
      // ready as soon as the admin scrolls down. Doesn't block the page render.
      void loadPlayback();
      // Так само у фоні: секція коментарів унизу сторінки, і чекати на неї
      // перед показом форми немає сенсу.
      void loadComments();
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) notFound = true;
      else error = 'Не вдалось завантажити матеріал';
    } finally {
      loading = false;
    }
  });

  const canSave = $derived(
    !loading &&
      !!original &&
      title.trim().length > 0 &&
      chaptersValid &&
      (title.trim() !== original.title ||
        (description.trim() || null) !== (original.description ?? null) ||
        (categoryId || null) !== (original.category?.id ?? null) ||
        isPublished !== original.isPublished ||
        JSON.stringify(body) !== JSON.stringify(original.body) ||
        JSON.stringify(chapters) !== JSON.stringify(original.chapters)),
  );

  /** Count of image URLs referenced by a Block[] — used by the auto-save
   *  diff to detect when the admin removed something destructively. */
  function imageCount(blocks: Block[]): number {
    let n = 0;
    for (const b of blocks) {
      if (b.type === 'image' && b.url) n += 1;
      else if (b.type === 'gallery') n += b.urls.length;
    }
    return n;
  }

  /** Bridge between BlockEditor and our local body state. When a removal is
   *  detected (block count drops OR image-URL count drops), fire an immediate
   *  PATCH so the DB stays consistent with what Bunny Storage actually has —
   *  the image was already deleted from Bunny when the admin confirmed the ×,
   *  so leaving the DB pointing to it would render a broken image for users.
   *
   *  Pure additions/edits (typing in a text block, uploading a new image)
   *  don't trigger auto-save — manual Save still handles those. */
  let autoSaving = $state(false);
  async function handleBodyChange(newBody: Block[]): Promise<void> {
    const removalHappened =
      newBody.length < body.length || imageCount(newBody) < imageCount(body);
    body = newBody;
    if (!removalHappened || !original) return;
    autoSaving = true;
    try {
      const patch: Parameters<typeof api.adminMaterialPatch>[1] = {
        body: newBody.length === 0 ? null : newBody,
      };
      const updated = await api.adminMaterialPatch(materialId, patch);
      // Sync `original` so canSave doesn't keep flagging the auto-saved diff
      // as an unsaved change.
      original = { ...original, body: updated.body };
    } catch (err) {
      // Quiet failure — manual Save is still there. Bunny image is already
      // gone, so the DB is just briefly stale.
      console.warn('[AdminMaterialEditPage] auto-save after removal failed', err);
    } finally {
      autoSaving = false;
    }
  }

  async function save(): Promise<void> {
    if (!canSave || saving || !original) return;
    if (isPublished && !categoryId) {
      alertOpen = true;
      return;
    }
    saving = true;
    error = null;
    try {
      const patch: Parameters<typeof api.adminMaterialPatch>[1] = {};
      if (title.trim() !== original.title) patch.title = title.trim();
      const newDesc = description.trim() || null;
      if (newDesc !== (original.description ?? null)) patch.description = newDesc;
      const newCatId = categoryId || null;
      if (newCatId !== (original.category?.id ?? null)) patch.categoryId = newCatId;
      if (isPublished !== original.isPublished) patch.isPublished = isPublished;
      if (JSON.stringify(body) !== JSON.stringify(original.body)) {
        patch.body = body.length === 0 ? null : body;
      }
      if (JSON.stringify(chapters) !== JSON.stringify(original.chapters)) {
        patch.chapters = chapters.length === 0 ? null : chapters;
      }
      await api.adminMaterialPatch(materialId, patch);
      backToMaterials();
    } catch (err) {
      error = err instanceof ApiError ? `Помилка (${err.code})` : 'Не вдалось зберегти';
    } finally {
      saving = false;
    }
  }

  // ── Коментарі під матеріалом ──────────────────────────────────────────────
  // Живуть окремо від форми і НЕ беруть участі в canSave-дифі з original:
  // видалення коментаря не має вмикати кнопку «Зберегти».
  const COMMENTS_PREVIEW = 10;
  let comments = $state<AdminCommentDTO[]>([]);
  let commentsTotal = $state(0);
  let commentsLoading = $state(true);
  let commentDeleteTarget = $state<AdminCommentDTO | null>(null);
  let commentBusy = $state(false);

  async function loadComments(): Promise<void> {
    commentsLoading = true;
    try {
      const res = await api.adminComments({ materialId, limit: COMMENTS_PREVIEW });
      comments = res.items;
      commentsTotal = res.total;
    } catch {
      /* секція другорядна — мовчки лишаємо порожньою */
    } finally {
      commentsLoading = false;
    }
  }

  async function doDeleteComment(): Promise<void> {
    if (!commentDeleteTarget) return;
    const target = commentDeleteTarget;
    commentBusy = true;
    try {
      await api.deleteComment(target.id);
      comments = comments.filter((c) => c.id !== target.id);
      commentsTotal = Math.max(0, commentsTotal - 1);
    } catch {
      /* мовчки: помилку видно з того, що рядок лишився на місці */
    } finally {
      commentBusy = false;
      commentDeleteTarget = null;
    }
  }

  function openAllComments(): void {
    adminState.commentsMaterialId = materialId;
    adminState.commentsStatus = 'all';
    adminState.commentsSearch = '';
    push('/admin/comments');
  }

  async function doDelete(): Promise<void> {
    if (deleting) return;
    deleting = true;
    deleteError = null;
    try {
      await api.adminDeleteMaterial(materialId);
      backToMaterials();
    } catch (err) {
      deleteError = err instanceof ApiError ? `Помилка (${err.code})` : 'Не вдалось видалити';
      deleting = false;
    }
  }
</script>

<div class="screen">
  <div class="admin-header">
    <a href="#/admin/dashboard" class="back-link">← Назад</a>
    <div class="header-title">Редагувати матеріал</div>
    {#if original && !loading && !notFound}
      <button
        type="button"
        class="header-preview"
        onclick={openPreview}
        aria-label="Перегляд"
        title="Перегляд"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
          <circle cx="12" cy="12" r="3"/>
        </svg>
      </button>
      {#if $session?.bunnyWritable}
      <button
        type="button"
        class="header-delete"
        disabled={deleting}
        onclick={() => { deleteError = null; deleteModalOpen = true; }}
        aria-label="Видалити матеріал"
        title="Видалити матеріал"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M3 6h18" />
          <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
          <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
          <path d="M10 11v6" />
          <path d="M14 11v6" />
        </svg>
      </button>
      {/if}
    {:else}
      <span></span>
    {/if}
  </div>

  <div class="scroll">
    {#if loading}
      <div class="placeholder">Завантажуємо…</div>
    {:else if notFound}
      <div class="placeholder">Матеріал не знайдено</div>
    {:else if original}
      {#if original.bunnyMissingAt}
        <div class="banner-error">
          Це відео більше не існує в Bunny — публікація знята автоматично. Перезалийте файл або видаліть матеріал.
        </div>
      {/if}

      <label class="field-label">Тип</label>
      <div class="readonly">{TYPE_LABELS[original.type]}</div>

      <label class="field-label">Розділ</label>
      <select class="field" bind:value={categoryId}>
        <option value="">— без розділу —</option>
        {#if categories}
          {#each categories as c (c.id)}
            <option value={c.id}>{c.name}</option>
          {/each}
        {/if}
      </select>

      <label class="field-label">Назва</label>
      <input class="field" maxlength="200" bind:value={title} />

      <label class="field-label">Опис</label>
      <MarkdownEditor bind:value={description} maxlength={2000} />

      {#if original.type === 'audio'}
        <label class="field-label">Аудіо</label>
        {#if original.bunnyMissingAt}
          <div class="player-missing"><Icon name="triangle-alert" size="xs" /> Файл втрачено в Bunny. Завантаж його ще раз через створення нового матеріалу.</div>
        {:else if playbackUrl}
          <!-- Inline player in the edit form: waveform + transport only, no
               chapter markers/list — those live in the ChapterEditor below
               so the two views don't fight over the same UI surface. -->
          <AudioPlayer
            src={playbackUrl}
            trackProgress={false}
            title={original.title}
            artist={selectedCategory?.name ?? ''}
          />
        {:else if playbackLoading}
          <div class="player-placeholder">Завантажуємо плеєр…</div>
        {:else}
          <div class="player-placeholder">Плеєр недоступний</div>
        {/if}
      {:else if original.type === 'video'}
        <label class="field-label">Відео</label>
        {#if original.bunnyMissingAt}
          <div class="player-missing"><Icon name="triangle-alert" size="xs" /> Файл втрачено в Bunny. Завантаж його ще раз через створення нового матеріалу.</div>
        {:else if playbackUrl}
          <div class="player-wrap">
            <iframe
              src={playbackUrl}
              title={original.title}
              allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture; fullscreen"
              allowfullscreen
            ></iframe>
          </div>
        {:else if playbackLoading}
          <div class="player-placeholder">Завантажуємо плеєр…</div>
        {:else}
          <div class="player-placeholder">Плеєр недоступний</div>
        {/if}
      {/if}

      {#if (original.type === 'audio' || original.type === 'video') && !original.bunnyMissingAt}
        <label class="field-label">Таймкоди</label>
        <ChapterEditor
          chapters={chapters}
          duration={original.durationSec}
          materialId={materialId}
          onChange={(v) => (chapters = v)}
          onValidityChange={(v) => (chaptersValid = v)}
        />
      {/if}

      <!-- Block-based body: required for text materials, optional supplementary
       *   content for video/audio (transcript, screenshots, related notes etc.). -->
      <label class="field-label">
        {original.type === 'text' ? 'Вміст матеріалу' : 'Додатковий вміст (необов\'язково)'}
      </label>
      <BlockEditor blocks={body} onChange={handleBodyChange} />
      {#if autoSaving}
        <div class="auto-save-hint">Зберігаємо…</div>
      {/if}

      <label class="field-label">Стан</label>
      <div class="status-pills">
        <button
          type="button"
          class="status-pill"
          class:active={!isPublished}
          onclick={() => (isPublished = false)}
        >Чернетка</button>
        <button
          type="button"
          class="status-pill"
          class:active={isPublished}
          onclick={() => (isPublished = true)}
        >Опубліковано</button>
      </div>

      {#if error}<div class="error">{error}</div>{/if}

      <button
        type="button"
        class="submit"
        disabled={!canSave || saving}
        onclick={save}
      >{saving ? 'Зберігаємо…' : 'Зберегти'}</button>

      <!-- div, а не label: це заголовок списку, а не підпис поля вводу. -->
      <div class="field-label">{commentsTotal > 0 ? `Коментарі (${commentsTotal})` : 'Коментарі'}</div>
      {#if commentsLoading}
        <div class="comments-empty">Завантажуємо…</div>
      {:else if comments.length === 0}
        <div class="comments-empty">Під цим матеріалом ще немає коментарів.</div>
      {:else}
        <div class="comments-list">
          {#each comments as c (c.id)}
            <div class="comment-row" class:pending={c.reportCount > 0 && !c.reviewedAt}>
              <Avatar photo={c.author.photoUrl} name={c.author.fullName} size={28} />
              <div class="comment-body">
                <div class="comment-head">
                  <span class="comment-name">{c.author.fullName}</span>
                  <span class="comment-date">{formatDateTime(c.createdAt)}</span>
                  {#if c.reportCount > 0 && !c.reviewedAt}
                    <span class="comment-flag"><Icon name="flag" size="xs" /> {c.reportCount}</span>
                  {/if}
                </div>
                <div class="comment-text">{c.text}</div>
              </div>
              <button
                type="button"
                class="comment-del"
                aria-label="Видалити коментар"
                disabled={commentBusy}
                onclick={() => (commentDeleteTarget = c)}
              >
                <Icon name="trash-2" size="xs" />
              </button>
            </div>
          {/each}
        </div>
        {#if commentsTotal > comments.length}
          <button type="button" class="comments-all" onclick={openAllComments}>
            Показати всі ({commentsTotal})
          </button>
        {/if}
      {/if}
    {/if}
  </div>

  <ConfirmModal
    open={commentDeleteTarget !== null}
    title="Видалити коментар?"
    message="Коментар зникне з клубу разом із відповідями на нього. У розділі «Коментарі» він лишиться у списку видалених."
    confirmLabel="Так, видалити"
    onConfirm={doDeleteComment}
    onCancel={() => (commentDeleteTarget = null)}
  />

  {#if alertOpen}
    <!-- svelte-ignore a11y_click_events_have_key_events a11y_no_static_element_interactions -->
    <div class="modal-backdrop" aria-hidden="true" onclick={() => (alertOpen = false)}></div>
    <div class="alert-sheet" role="alertdialog" aria-modal="true" aria-label="Помилка">
      <div class="alert-title">Не можна опублікувати</div>
      <div class="alert-body">Спочатку оберіть розділ для матеріалу — без нього він не показується користувачам.</div>
      <button type="button" class="alert-ok" onclick={() => (alertOpen = false)}>Зрозуміло</button>
    </div>
  {/if}

  {#if previewOpen && original}
    <!-- svelte-ignore a11y_click_events_have_key_events a11y_no_static_element_interactions -->
    <div class="modal-backdrop" onclick={() => (previewOpen = false)}></div>
    <div class="preview-sheet" role="dialog" aria-modal="true" aria-label="Перегляд">
      <div class="modal-head">
        <div class="modal-title">Перегляд для користувача</div>
        <button type="button" class="modal-close" onclick={() => (previewOpen = false)}><Icon name="x" /></button>
      </div>
      <div class="modal-body preview-page">
        <div class="preview-topbar">
          {selectedCategory
            ? selectedCategory.name
            : '— без розділу —'}
        </div>
        <div class="preview-content">
          <h1 class="title">{title.trim() || '(без назви)'}</h1>
          {#if description.trim()}
            <div class="description md-content">{@html renderMarkdown(description)}</div>
          {/if}
          {#if original.type === 'audio' && playbackUrl}
            <AudioPlayer
              src={playbackUrl}
              trackProgress={false}
              chapters={chapters}
              title={title.trim() || original.title}
              artist={selectedCategory?.name ?? ''}
            />
          {:else if original.type === 'video' && playbackUrl}
            <div class="player-wrap">
              <iframe
                src={playbackUrl}
                title="Перегляд"
                allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture; fullscreen"
                allowfullscreen
              ></iframe>
            </div>
          {:else if (original.type === 'video' || original.type === 'audio') && playbackLoading}
            <div class="placeholder">
              <div class="placeholder-text">Завантажуємо плеєр…</div>
            </div>
          {:else if original.type === 'video' || original.type === 'audio'}
            <div class="placeholder">
              <div class="placeholder-icon"><Icon name={original.type === 'video' ? 'circle-play' : 'audio-lines'} size="xl" /></div>
              <div class="placeholder-text">Плеєр недоступний</div>
            </div>
          {/if}

          <!-- Body blocks under the player for all types — text-only materials
               only show this section, video/audio show it AS supplementary
               content after the main media. -->
          {#if body.length === 0 && original.type === 'text'}
            <div class="placeholder">
              <div class="placeholder-icon"><Icon name="file-text" size="xl" /></div>
              <div class="placeholder-text">Контент не заповнено</div>
            </div>
          {:else}
            {#each body as block (block.id)}
              {#if block.type === 'text'}
                <div class="text-content md-content">{@html renderMarkdown(block.markdown)}</div>
              {:else if block.type === 'image' && block.url}
                <!-- svelte-ignore a11y_click_events_have_key_events a11y_no_noninteractive_element_interactions -->
                <img
                  class="preview-cover clickable width-{block.width ?? 'full'}"
                  src={block.url}
                  alt=""
                  onclick={() => openLightbox([block.url], 0)}
                />
              {:else if block.type === 'gallery' && block.urls.length > 0}
                <div class="preview-media-group cols-{block.cols && block.urls.length >= block.cols ? block.cols : 2}">
                  {#each block.urls as src, i (src + i)}
                    <!-- svelte-ignore a11y_click_events_have_key_events a11y_no_noninteractive_element_interactions -->
                    <img
                      class="clickable"
                      src={src}
                      alt=""
                      onclick={() => openLightbox(block.urls, i)}
                    />
                  {/each}
                </div>
              {/if}
            {/each}
          {/if}
          <div class="preview-state-row">
            <span class="preview-state-pill" class:pub={isPublished}>
              {#if isPublished}<Icon name="check" size="xs" /> Опубліковано{:else}◌ Чернетка{/if}
            </span>
            {#if !categoryId}
              <span class="preview-state-pill warn"><Icon name="triangle-alert" size="xs" /> Без розділу — користувачам не показано</span>
            {/if}
          </div>
        </div>
      </div>
    </div>
  {/if}

  {#if deleteModalOpen && original}
    <!-- svelte-ignore a11y_click_events_have_key_events a11y_no_static_element_interactions -->
    <div class="modal-backdrop" onclick={() => !deleting && (deleteModalOpen = false)}></div>
    <div class="modal-sheet" role="dialog" aria-modal="true">
      <div class="modal-head">
        <div class="modal-title">Видалити матеріал?</div>
        <button
          type="button"
          class="modal-close"
          disabled={deleting}
          onclick={() => (deleteModalOpen = false)}
        ><Icon name="x" /></button>
      </div>
      <div class="modal-body">
        <p class="modal-desc">
          <strong>{original.title}</strong> буде видалений з апки{#if original.type !== 'text'}, а відповідне відео — з Bunny{/if}.
        </p>
        <p class="modal-desc warn">
          Цю дію не можна скасувати.
        </p>
        {#if deleteError}<div class="error">{deleteError}</div>{/if}
      </div>
      <div class="modal-actions">
        <button
          type="button"
          class="modal-btn modal-btn-secondary"
          disabled={deleting}
          onclick={() => (deleteModalOpen = false)}
        >Скасувати</button>
        <button
          type="button"
          class="modal-btn modal-btn-primary"
          disabled={deleting}
          onclick={doDelete}
        >{deleting ? 'Видаляю…' : 'Так, видалити'}</button>
      </div>
    </div>
  {/if}

  {#if lightbox}
    <Lightbox
      images={lightbox.images}
      initialIndex={lightbox.index}
      onClose={() => (lightbox = null)}
    />
  {/if}
</div>

<style>
  /* ── Коментарі під матеріалом ─────────────────────────────────────────── */
  .comments-empty {
    padding: 14px;
    font-size: 12px;
    color: var(--text-muted);
    background: var(--bg2);
    border: 1px dashed var(--border2);
    border-radius: 10px;
  }
  .comments-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .comment-row {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    background: var(--bg2);
    border: 1px solid var(--border);
    border-radius: 10px;
    padding: 10px;
  }
  .comment-row.pending {
    border-left: 3px solid var(--heart);
    background: var(--pu-light);
  }
  .comment-body {
    flex: 1;
    min-width: 0;
  }
  .comment-head {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .comment-name {
    font-size: 12px;
    font-weight: 600;
    color: var(--text);
  }
  .comment-date {
    font-size: 11px;
    color: var(--faint);
  }
  .comment-flag {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    background: var(--heart);
    color: var(--text-light);
    border-radius: 999px;
    padding: 2px 7px;
    font-size: 10px;
    font-weight: 600;
  }
  .comment-text {
    margin-top: 3px;
    font-size: 12px;
    line-height: 1.45;
    color: var(--text);
    white-space: pre-wrap;
    overflow-wrap: anywhere;
  }
  .comment-del {
    background: none;
    border: none;
    padding: 4px;
    cursor: pointer;
    color: var(--status-inactive);
    display: inline-flex;
    flex-shrink: 0;
  }
  .comment-del:disabled {
    opacity: 0.5;
    cursor: default;
  }
  .comments-all {
    width: 100%;
    margin-top: 8px;
    padding: 9px;
    background: var(--bg2);
    border: 1px solid var(--border);
    border-radius: 10px;
    font-family: inherit;
    font-size: 12px;
    color: var(--text);
    cursor: pointer;
  }
  .screen {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    background: var(--bg);
  }
  .admin-header {
    background: linear-gradient(135deg, var(--dark), var(--dark3));
    padding: 14px 18px;
    color: var(--text-light);
    display: flex;
    align-items: center;
    gap: 10px;
    flex-shrink: 0;
  }
  .back-link { font-size: 12px; color: rgba(251, 246, 236,0.9); text-decoration: none; }
  .header-title { flex: 1; text-align: center; font-size: 14px; font-weight: 600; }
  .header-delete,
  .header-preview {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    color: var(--text-light);
    cursor: pointer;
    font-family: inherit;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .header-preview {
    background: rgba(251, 246, 236, 0.15);
    border: 1px solid rgba(251, 246, 236, 0.25);
    margin-right: 4px;
  }
  .header-preview:hover { background: rgba(251, 246, 236, 0.25); }
  .header-preview svg { color: var(--text-light); display: block; }
  .header-delete {
    background: rgba(168, 80, 47, 0.18);
    border: 1px solid rgba(168, 80, 47, 0.35);
  }
  .header-delete:hover { background: rgba(168, 80, 47, 0.3); }
  .header-delete:disabled { opacity: 0.5; cursor: not-allowed; }
  .header-delete svg { color: var(--text-light); display: block; }

  .scroll { flex: 1; overflow-y: auto; padding: 18px 18px 100px; }
  .field-label {
    display: block;
    font-size: 11px;
    font-weight: 600;
    color: var(--text-muted);
    margin: 12px 0 6px;
    letter-spacing: 0.5px;
    text-transform: uppercase;
  }
  .field {
    width: 100%;
    border: 1px solid var(--border2);
    border-radius: 10px;
    padding: 11px 13px;
    font-size: 13px;
    color: var(--text);
    background: var(--bg);
    font-family: inherit;
    outline: none;
    margin-bottom: 4px;
  }
  .field:focus { border-color: var(--outline); }
  textarea.field { resize: vertical; min-height: 80px; }
  .readonly {
    padding: 11px 13px;
    font-size: 13px;
    color: var(--text-muted);
    background: var(--bg2);
    border: 1px solid var(--border);
    border-radius: 10px;
  }
  .status-pills {
    display: flex;
    gap: 6px;
    margin: 4px 0 8px;
  }
  .status-pill {
    flex: 1;
    border: 1px solid var(--border2);
    background: var(--bg);
    color: var(--text-muted);
    padding: 9px 12px;
    border-radius: 10px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    font-family: inherit;
    transition: background 0.15s, color 0.15s, border-color 0.15s;
  }
  .status-pill.active {
    background: var(--pu);
    color: var(--text-light);
    border-color: var(--pu);
  }
  /* Inline media player rendered directly in the edit form so admin can
   * see what they uploaded without opening the preview modal. */
  .player-wrap {
    position: relative;
    width: 100%;
    aspect-ratio: 16 / 9;
    border-radius: 12px;
    overflow: hidden;
    background: black;
    margin-bottom: 14px;
  }
  .player-wrap iframe {
    width: 100%;
    height: 100%;
    border: 0;
    display: block;
  }
  .player-placeholder {
    padding: 30px 16px;
    text-align: center;
    background: var(--bg2);
    border: 1px dashed var(--border2);
    border-radius: 12px;
    color: var(--text-muted);
    font-size: 13px;
    margin-bottom: 14px;
  }
  .player-missing {
    padding: 14px 16px;
    background: rgba(196, 99, 62, 0.08);
    border: 1px solid rgba(196, 99, 62, 0.3);
    border-radius: 12px;
    color: var(--heart);
    font-size: 13px;
    margin-bottom: 14px;
    line-height: 1.45;
  }
  /* Small "Зберігаємо…" pill shown under the block editor whenever a
   * removal triggers an auto-save. Quiet, dismisses itself when done. */
  .auto-save-hint {
    display: inline-block;
    margin: 0 0 14px;
    padding: 4px 10px;
    background: var(--pu-light);
    color: var(--accent-text);
    font-size: 11px;
    font-weight: 500;
    border-radius: 999px;
  }
  .modal-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.45);
    z-index: 90;
  }
  .alert-sheet {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: calc(100% - 48px);
    max-width: 320px;
    background: var(--bg);
    border-radius: 16px;
    padding: 20px;
    z-index: 91;
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.25);
  }
  .alert-title {
    font-size: 15px;
    font-weight: 600;
    color: var(--text);
    margin-bottom: 8px;
  }
  .alert-body {
    font-size: 13px;
    color: var(--text-muted);
    line-height: 1.45;
    margin-bottom: 16px;
  }
  .alert-ok {
    width: 100%;
    padding: 12px;
    border: none;
    border-radius: 10px;
    background: linear-gradient(135deg, var(--pu), var(--pu3));
    color: var(--text-light);
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    font-family: inherit;
  }
  .error {
    margin: 12px 0;
    padding: 10px 14px;
    background: var(--danger-bg);
    color: var(--status-inactive);
    border: 1px solid var(--danger-border);
    border-radius: 10px;
    font-size: 12px;
  }
  .banner-error {
    margin-bottom: 8px;
    padding: 12px 14px;
    background: var(--status-inactive);
    color: var(--text-light);
    border-radius: 10px;
    font-size: 12px;
    font-weight: 500;
    line-height: 1.45;
  }
  .submit {
    width: 100%;
    margin-top: 18px;
    padding: 14px;
    border: none;
    border-radius: 12px;
    background: linear-gradient(135deg, var(--pu), var(--pu3));
    color: var(--text-light);
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    font-family: inherit;
  }
  .submit:disabled { opacity: 0.5; cursor: not-allowed; }
  .placeholder {
    padding: 30px 20px;
    text-align: center;
    color: var(--text-muted);
    font-size: 13px;
  }

  .modal-sheet {
    position: fixed;
    left: 50%;
    bottom: 0;
    transform: translateX(-50%);
    width: min(100%, 480px);
    max-height: 85vh;
    background: var(--bg);
    border-radius: 16px 16px 0 0;
    z-index: 1001;
    display: flex;
    flex-direction: column;
    box-shadow: 0 -16px 40px rgba(0, 0, 0, 0.25);
    animation: sheet-up 0.25s cubic-bezier(0.22, 1, 0.36, 1);
  }
  @keyframes sheet-up {
    from { transform: translate(-50%, 100%); }
    to { transform: translate(-50%, 0); }
  }
  .modal-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 18px 12px;
    border-bottom: 0.5px solid var(--border);
    flex-shrink: 0;
  }
  .modal-title { font-size: 16px; font-weight: 700; color: var(--text); }
  .modal-close {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    background: var(--bg2);
    border: none;
    color: var(--text-muted);
    cursor: pointer;
    font-size: 14px;
    font-family: inherit;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .modal-close:disabled { opacity: 0.5; cursor: not-allowed; }
  .modal-body {
    padding: 14px 18px 18px;
    overflow-y: auto;
    flex: 1;
    min-height: 0;
  }
  .modal-desc {
    margin: 0 0 14px;
    font-size: 13px;
    color: var(--text);
    line-height: 1.5;
  }
  .modal-desc.warn {
    color: var(--status-expiring);
    background: var(--warn-bg);
    border: 1px solid var(--warn-border);
    padding: 10px 12px;
    border-radius: 10px;
  }
  .modal-actions {
    display: flex;
    gap: 8px;
    padding: 12px 18px calc(16px + env(safe-area-inset-bottom));
    border-top: 0.5px solid var(--border);
    flex-shrink: 0;
  }
  .modal-btn {
    flex: 1;
    border: none;
    padding: 12px;
    border-radius: 10px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    font-family: inherit;
  }
  .modal-btn-secondary {
    background: var(--bg2);
    color: var(--text);
    border: 1px solid var(--border2);
  }
  .modal-btn-primary {
    background: linear-gradient(135deg, var(--status-inactive), var(--status-inactive));
    color: var(--text-light);
  }
  .modal-btn:disabled { opacity: 0.5; cursor: not-allowed; }

  /* === Preview modal === */
  .preview-sheet {
    position: fixed;
    left: 50%;
    bottom: 0;
    transform: translateX(-50%);
    width: min(100%, 480px);
    max-height: 92vh;
    background: var(--bg);
    border-radius: 16px 16px 0 0;
    z-index: 1001;
    display: flex;
    flex-direction: column;
    box-shadow: 0 -16px 40px rgba(0, 0, 0, 0.25);
    animation: sheet-up 0.25s cubic-bezier(0.22, 1, 0.36, 1);
  }
  /* Mirror MaterialPage layout so admin sees a faithful preview. */
  .preview-page {
    padding: 0;
  }
  .preview-topbar {
    padding: 12px 18px;
    background: linear-gradient(135deg, var(--dark), var(--dark3));
    color: var(--text-light);
    font-size: 13px;
    font-weight: 600;
    text-align: center;
  }
  .preview-content {
    padding: 18px 18px 100px;
  }
  .preview-cover {
    width: 100%;
    height: auto;
    border-radius: 12px;
    margin-bottom: 14px;
    display: block;
  }
  .preview-cover.width-medium {
    width: 70%;
    margin-left: auto;
    margin-right: auto;
  }
  .preview-cover.width-small {
    width: 45%;
    margin-left: auto;
    margin-right: auto;
  }
  .preview-media-group {
    display: grid;
    gap: 8px;
    margin-top: 14px;
    margin-bottom: 18px;
  }
  .preview-media-group.cols-2 { grid-template-columns: repeat(2, 1fr); }
  .preview-media-group.cols-3 { grid-template-columns: repeat(3, 1fr); }
  .preview-media-group.cols-4 { grid-template-columns: repeat(4, 1fr); }
  .preview-content :global(.clickable) {
    cursor: zoom-in;
  }
  .preview-media-group img {
    width: 100%;
    aspect-ratio: 1;
    object-fit: cover;
    border-radius: 10px;
    display: block;
  }
  .preview-content .title {
    font-size: 22px;
    font-weight: 700;
    color: var(--text);
    line-height: 1.3;
    margin: 0 0 16px;
  }
  .preview-content .description {
    font-size: 13px;
    line-height: 1.6;
    color: var(--text-muted);
    margin-bottom: 14px;
  }
  .preview-content .text-content {
    font-size: 14px;
    line-height: 1.7;
    color: var(--text);
    margin-bottom: 18px;
    padding: 16px;
    background: var(--bg2);
    border: 1px solid var(--border);
    border-radius: 12px;
  }
  .preview-content .player-wrap {
    position: relative;
    width: 100%;
    aspect-ratio: 16 / 9;
    border-radius: 12px;
    overflow: hidden;
    background: black;
    margin-bottom: 18px;
  }
  .preview-content .player-wrap iframe {
    width: 100%;
    height: 100%;
    border: 0;
    display: block;
  }
  .preview-content .placeholder {
    padding: 40px 20px;
    text-align: center;
    background: var(--bg2);
    border: 1px dashed var(--border2);
    border-radius: 12px;
    margin-bottom: 18px;
  }
  .preview-content .placeholder-icon {
    font-size: 36px;
    margin-bottom: 8px;
  }
  .preview-content .placeholder-text {
    font-size: 12px;
    color: var(--text-muted);
  }
  .preview-state-row {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }
  .preview-state-pill {
    font-size: 11px;
    font-weight: 600;
    padding: 4px 10px;
    border-radius: 999px;
    background: var(--bg2);
    color: var(--text-muted);
    border: 1px solid var(--border);
  }
  .preview-state-pill.pub {
    background: var(--green-light);
    color: var(--green);
    border-color: var(--green-light);
  }
  .preview-state-pill.warn {
    background: var(--warn-bg);
    color: var(--status-expiring);
    border-color: var(--warn-border);
  }
</style>
